import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Fab } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loading } from '../../shared/components/Loading';
import { ReloadScreen } from '../../shared/components/ReloadScreen';
import { useEstablishment } from '../../shared/contexts';
import { useBag } from '../../shared/contexts/BagContext';
import { Group, Product } from '../../shared/entities';
import { AppError } from '../../shared/errors';
import { useDidUpdate } from '../../shared/hooks/did_update';
import { ProductRepository } from '../../shared/repositories';
import { BagCard } from './components/BagCard';
import { GroupProducts } from './components/GroupProducts';
import { Groups } from './components/Groups';
import { NoProductsFound } from './components/NoProductsFound';
import { Products } from './components/Products';
import { SearchField } from './components/SearchField';
import { pageSize } from './constants';
import { PizzaPage } from '../pizza';
import { PizzaContextProvider } from '../pizza/PizzaContext';
import { EstablishmentClosedDialog } from './components/EstablishmentClosedDialog';

type Props = {
	productRepository: ProductRepository;
};

export function MenuPage({ productRepository }: Props) {
	const { state } = useBag();
	const { establishment, establishmentClosed } = useEstablishment().state;
	const [groups, setGroups] = useState<Group[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
	const [selectedSubGroup, setSelectedSubGroup] = useState<Group | null>(null);
	const prevSelectedSubGroupRef = useRef<Group | null>(null);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [menuError, setMenuError] = useState<AppError | null>(null);
	const [page, setPage] = useState({ value: 1 }); // Força o trigger do useEffect mesmo quando o valor não muda
	const [hasMore, setHasMore] = useState<boolean>(true);
	const [pendingPizzaGroup, setPendingPizzaGroup] = useState<Group | null>(null);
	const [closedWarningShown, setClosedWarningShown] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	function loadMenu() {
		setMenuError(null);
		setIsLoading(true);
		productRepository
			.getGroups()
			.then((loadedGroups) => {
				const sortedGroups = [...loadedGroups].sort(
					(a, b) => (a.defaultCategory ? -1 : 0) - (b.defaultCategory ? -1 : 0)
				);
				setGroups(sortedGroups);

				const groupIdParam = searchParams.get('group');
				const previousGroup = loadedGroups.find(
					(g) => String(g.id) === groupIdParam
				);
				if (previousGroup) {
					return applySelectedGroup(previousGroup);
				}

				const defaultGroup = loadedGroups.find((g) => g.defaultCategory);
				if (defaultGroup) {
					selectGroup(defaultGroup);
				}
			})
			.catch((error) => setMenuError(error as AppError))
			.finally(() => setIsLoading(false));

		loadProducts();
	}

	async function loadProducts() {
		await productRepository
			.getProducts({
				text: searchTerm,
				page: page.value,
				size: pageSize,
			})
			.then((newProducts) => {
				setHasMore(newProducts.length >= pageSize);
				if (page.value === 1) {
					setProducts(newProducts);
				} else {
					setProducts([...products, ...newProducts]);
				}
			});
	}

	useDidUpdate(() => {
		setIsLoading(true);
		loadProducts().finally(() => setIsLoading(false));
	}, [page]);

	function loadMore() {
		setPage({ value: page.value + 1 });
	}

	function selectGroup(group: Group | null) {
		if (group?.isPizza && establishmentClosed && !closedWarningShown) {
			setClosedWarningShown(true);
			return setPendingPizzaGroup(group);
		}
		applySelectedGroup(group);
	}

	function closeEstablishmentClosedDialog() {
		const group = pendingPizzaGroup;
		setPendingPizzaGroup(null);
		applySelectedGroup(group);
	}

	function applySelectedGroup(group: Group | null) {
		setSelectedGroup(group);
		setSearchParams(group ? { group: String(group.id) } : {}, {
			replace: true,
		});
		if (group) {
			const element = document.getElementById(group.archorLink);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}

	useEffect(() => {
		loadMenu();
	}, []);

	useEffect(() => {
		if (selectedSubGroup == null && prevSelectedSubGroupRef.current != null) {
			setTimeout(() => scrollToTop(), 50);
		}
		prevSelectedSubGroupRef.current = selectedSubGroup;
		if (selectedSubGroup) {
			const element = document.getElementById(selectedSubGroup.archorLink);
			if (element) {
				let position = element?.getBoundingClientRect();
				if (!position) return;
				window.scrollBy({ behavior: 'smooth', top: position.top - 250 });
			}
		}
	}, [selectedSubGroup]);

	function scrollToTop() {
		const targetElement = document.getElementsByTagName('html')[0];
		let position = targetElement?.getBoundingClientRect();
		if (!position) return;
		window.scrollBy({ behavior: 'smooth', top: position.top });
	}

	useDidUpdate(() => {
		if (!selectedGroup) {
			setPage({ value: 1 });
		}
	}, [searchTerm]);

	function openWhatsApp() {
		const url = `https://wa.me/${establishment.phone}`;
		window.open(url, '_blank');
	}

	const isInitialLoading = isLoading && !groups.length && !products.length;

	if (isInitialLoading) {
		return <Loading />;
	} else if (menuError != null) {
		return <ReloadScreen message={menuError.message} onReload={loadMenu} />;
	}

	return (
		<Box paddingBottom={20}>
			<EstablishmentClosedDialog
				open={pendingPizzaGroup != null}
				close={closeEstablishmentClosedDialog}
			/>
			<div
				style={{
					padding: '8px 20px 0 20px',
					position: 'fixed',
					width: 'calc(100% - 40px)',
					backgroundColor: 'white',
					zIndex: 1,
				}}
			>
				<SearchField searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
				<Groups
					groups={groups}
					selectedGroup={selectedGroup}
					selectedSubGroup={selectedSubGroup}
					onGroupPressed={selectGroup}
					onSubGroupPressed={setSelectedSubGroup}
				/>
			</div>

			<Box
				top={
					selectedGroup && selectedGroup.subgroups.length > 0
						? '160px'
						: '120px'
				}
				position="relative"
			>
				{(() => {
					if (!products && searchTerm) {
						return <NoProductsFound />;
					}

					if (isLoading && groups.length && page.value === 1) {
						return <Loading />;
					}

					if (products.length === 0) {
						return <NoProductsFound />;
					}

					let mainContent;
					if (selectedGroup == null) {
						mainContent = (
							<Products
								products={products}
								hasMore={hasMore}
								isLoading={isLoading}
								loadMore={loadMore}
							/>
						);
					} else if (selectedGroup.isPizza) {
						mainContent = (
							<PizzaContextProvider productRepository={productRepository}>
								<PizzaPage inline/>
							</PizzaContextProvider>
						);
					} else {
						mainContent = (
							<>
								{[selectedGroup, ...selectedGroup.subgroups].map((group) => (
									<GroupProducts
										key={`group+${group.id}`}
										productRepository={productRepository}
										search={searchTerm}
										group={group}
									/>
								))}
							</>
						);
					}

					return mainContent;
				})()}
				{!state.isEmpty && <BagCard />}
			</Box>
			{establishment.isDelivery && establishment.phone && (
				<Fab
					onClick={openWhatsApp}
					sx={{
						position: 'fixed',
						bottom: 70,
						right: 16,
						backgroundColor: '#075E54',
						color: '#FFF',
						'&:hover': {
							backgroundColor: '#128C7E',
						},
					}}
				>
					<WhatsAppIcon />
				</Fab>
			)}
		</Box>
	);
}
